// paxsudos/upload-by-url/uplink_attach.js
// Uplink: backend streamaa median suoraan Mastodonin /api/v2/media -päähän.
// Frontti näyttää saman upload-progressin kuin normaalissa drag&dropissa.

import api from 'mastodon/api';
import { store } from 'mastodon/store';
import {
  uploadComposeRequest,
  uploadComposeProgress,
  uploadComposeSuccess,
  uploadComposeFail,
} from 'mastodon/actions/compose';

function getCSRF() {
  const m = document.querySelector('meta[name="csrf-token"]');
  return m ? m.content : '';
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

// Pollaa /api/v1/media/:id kunnes valmis (HTTP 200), muuten heittää virheen timeoutissa
async function pollMediaReady(id, { timeoutMs = 90_000 } = {}) {
  const t0 = Date.now();
  let tries = 1;

  for (;;) {
    const resp = await api().get(`/api/v1/media/${id}`).catch((e) => e.response || { status: 0 });

    // Mastodon palauttaa usein 206 (Partial Content) prosessoinnin aikana
    if (resp && resp.status === 200 && resp.data) {
      return resp.data;
    }

    const elapsed = Date.now() - t0;
    if (elapsed > timeoutMs) {
      throw new Error('media processing timeout');
    }

    // Kevyt eksponentiaalinen backoff
    const wait = Math.min(3000, 500 + Math.floor(Math.log2(Math.max(2, tries)) * 500));
    tries += 1;
    await sleep(wait);
  }
}

/**
 * Liitä URL mediaksi käyttäen uplinkiä (backend streamaa → Mastodon).
 * Näyttää edistymisen composerissa (uploadComposeProgress).
 */
export async function uplinkAttachWithProgress(url) {
  // 1) käynnistä uplink-job
  store.dispatch(uploadComposeRequest());

  let jobId = null;
  let total = null;

  try {
    const r = await fetch('/upload-by-url/uplink', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'content-type': 'application/json',
        'x-csrf-token': getCSRF(),
      },
      body: JSON.stringify({ url }),
    });

    const json = await r.json().catch(() => ({}));
    if (!r.ok || !json || json.ok !== true || !json.job_id) {
      const msg = (json && json.error) ? json.error : `uplink start failed (${r.status})`;
      throw new Error(msg);
    }

    jobId = json.job_id;
    total = json.total || null; // voi puuttua jos Content-Length ei tiedossa
  } catch (e) {
    store.dispatch(uploadComposeFail(e));
    throw e;
  }

  // 2) pollaa job-statusta ja päivitä progress
  let lastSent = -1;
  const startedAt = Date.now();

  for (;;) {
    let st;
    try {
      const rs = await fetch(`/upload-by-url/uplink/status?job_id=${encodeURIComponent(jobId)}`, {
        method: 'GET',
        credentials: 'include',
        headers: { accept: 'application/json' },
      });
      st = await rs.json().catch(() => null);
      if (!rs.ok || !st || st.ok !== true) {
        const emsg = st && st.error ? st.error : `uplink status ${rs.status}`;
        throw new Error(emsg);
      }
    } catch (e) {
      store.dispatch(uploadComposeFail(e));
      throw e;
    }

    // progress
    const sent = typeof st.sent === 'number' ? st.sent : 0;
    const tot  = (typeof st.total === 'number' ? st.total : total);

    if (typeof tot === 'number' && sent !== lastSent) {
      store.dispatch(uploadComposeProgress(sent, tot));
      lastSent = sent;
    }

    // tilat: uploading | processing | done | error
    if (st.state === 'uploading') {
      // backoff: 1s → 3s
      const elapsed = Date.now() - startedAt;
      const wait = Math.min(3000, 1000 + Math.floor(elapsed / 30));
      await sleep(wait);
      continue;
    }

    if (st.state === 'error') {
      const err = new Error(st.error || 'uplink error');
      store.dispatch(uploadComposeFail(err));
      throw err;
    }

    if ((st.state === 'processing' || st.state === 'done') && st.media) {
      // 3) jos Mastodon ilmoitti 202 (processing), odotetaan kunnes /api/v1/media/:id palauttaa 200
      try {
        const mediaFull = await pollMediaReady(st.media.id);
        store.dispatch(uploadComposeSuccess(mediaFull, null));
      } catch (_) {
        // fallback: näytä se mitä meillä on (Mastodon päivittää sen myöhemmin itse)
        store.dispatch(uploadComposeSuccess(st.media, null));
      }
      break;
    }

    // varalta: jos tila on tuntematon, pieni viive
    await sleep(1000);
  }

  return true;
}

// Yhteensopivuusnimi (jos haluat kutsua lyhyemmin)
export const uplinkAttach = uplinkAttachWithProgress;
